'use client';

/**
 * Provider Matching Panel Component
 *
 * Displays matched providers for a service referral and allows:
 * - Viewing auto-matched providers from provider directory and CBOs
 * - Selecting a provider for the referral
 * - Triggering re-matching if needed
 * - Manually adding providers from Provider Directory (full experience)
 * - Removing matched providers
 * - Communicating with providers via multiple channels
 * - Voice AI response option for providers
 * - Showing match scores and distance
 *
 * Integrates with the employee_referral_matching backend service
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Building2,
  MapPin,
  Star,
  Phone,
  Mail,
  Check,
  AlertCircle,
  Loader2,
  RefreshCw,
  Users,
  Clock,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ThumbsUp,
  ThumbsDown,
  Send,
  Shield,
  Plus,
  Search,
  Trash2,
  MessageSquare,
  MoreVertical,
  ExternalLink,
  PhoneCall,
  X,
  Printer,
  ShieldCheck,
  ShieldAlert,
  History,
} from 'lucide-react';
import { serviceReferralsAPI, ServiceReferral } from '@/services/api/serviceReferrals';
import { apiClient } from '@/services/apiClient';
import { ProviderSearch } from '@/components/provider-directory/ProviderSearch';
import { WrenApprovalBanner } from './WrenApprovalBanner';
import { ProviderResults, Provider } from '@/components/provider-directory/ProviderResults';
import { ProviderDetail } from '@/components/provider-directory/ProviderDetail';
import { providerDirectoryAPI, SearchFilters } from '@/services/providerDirectoryApi';
import { ProviderCommunicationDialog } from './ProviderCommunicationDialog';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ProviderMatch {
  id: number;
  provider_type: 'cbo' | 'provider_directory';
  provider_id: string | number;
  provider_name: string;
  match_score: number;
  distance_miles?: number;
  distance_score?: number;
  service_score?: number;
  match_rank: number;
  is_selected: boolean;
  response_status?: 'pending' | 'accepted' | 'declined' | 'negotiated';
  notified_at?: string;
  response_at?: string;
  provider_phone?: string;
  provider_email?: string;
  provider_fax?: string;
  provider_address?: string;
  phi_released?: boolean; // PHI is only released after provider accepts
  communication_count?: number; // Number of outreach attempts
  last_communication_at?: string;
}

interface DirectoryProvider {
  id: string;
  organization_name: string;
  organization_type?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  primary_phone?: string;
  primary_email?: string;
  distance_miles?: number;
  services?: string[];
}

interface ProviderMatchingPanelProps {
  referralId: number;
  referral: ServiceReferral;
  readOnly?: boolean;
  onProviderSelected?: (match: ProviderMatch) => void;
  onMatchingTriggered?: () => void;
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ProviderMatchingPanel({
  referralId,
  referral,
  readOnly = false,
  onProviderSelected,
  onMatchingTriggered,
  className = '',
}: ProviderMatchingPanelProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [matches, setMatches] = useState<ProviderMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [matchingInProgress, setMatchingInProgress] = useState(false);
  const [selectingProvider, setSelectingProvider] = useState<number | null>(null);
  const [notifyingProvider, setNotifyingProvider] = useState(false);
  const [expandedProvider, setExpandedProvider] = useState<number | null>(null);

  // Add provider from directory state - Full Provider Directory experience
  const [showProviderDirectory, setShowProviderDirectory] = useState(false);
  const [directoryProviders, setDirectoryProviders] = useState<Provider[]>([]);
  const [isSearchingDirectory, setIsSearchingDirectory] = useState(false);
  const [selectedDirectoryProvider, setSelectedDirectoryProvider] = useState<Provider | null>(null);
  const [addingProvider, setAddingProvider] = useState<string | null>(null);

  // Remove provider state
  const [removingProvider, setRemovingProvider] = useState<number | null>(null);

  // Voice AI call state
  const [initiatingVoiceCall, setInitiatingVoiceCall] = useState<number | null>(null);

  // Communication dialog state
  const [communicationDialogOpen, setCommunicationDialogOpen] = useState(false);
  const [selectedMatchForCommunication, setSelectedMatchForCommunication] = useState<ProviderMatch | null>(null);
  const [initialCommunicationChannel, setInitialCommunicationChannel] = useState<'email' | 'sms' | 'phone' | 'voice_ai'>('email');

  // Open communication dialog
  const openCommunicationDialog = (match: ProviderMatch, channel: 'email' | 'sms' | 'phone' | 'voice_ai' = 'email') => {
    setSelectedMatchForCommunication(match);
    setInitialCommunicationChannel(channel);
    setCommunicationDialogOpen(true);
  };

  // Handle communication sent
  const handleCommunicationSent = () => {
    loadMatches();
    setSuccessMessage('Communication sent successfully');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Load matches
  const loadMatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<ProviderMatch[]>(
        `/api/service-referrals/${referralId}/matches`
      );
      setMatches(response.data);
    } catch (err) {
      console.error('Failed to load matches:', err);
      setError('Failed to load provider matches');
    } finally {
      setLoading(false);
    }
  }, [referralId]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  // Trigger matching
  const handleTriggerMatching = async () => {
    setMatchingInProgress(true);
    setError(null);
    try {
      await apiClient.post(`/api/service-referrals/${referralId}/auto-match`);
      await loadMatches();
      onMatchingTriggered?.();
    } catch (err) {
      console.error('Failed to trigger matching:', err);
      setError('Failed to run provider matching');
    } finally {
      setMatchingInProgress(false);
    }
  };

  // Select provider
  const handleSelectProvider = async (matchId: number) => {
    setSelectingProvider(matchId);
    try {
      await apiClient.post(`/api/service-referrals/${referralId}/matches/${matchId}/select`);
      await loadMatches();
      const selectedMatch = matches.find(m => m.id === matchId);
      if (selectedMatch) {
        onProviderSelected?.(selectedMatch);
      }
    } catch (err) {
      console.error('Failed to select provider:', err);
      setError('Failed to select provider');
    } finally {
      setSelectingProvider(null);
    }
  };

  // Notify selected provider
  const handleNotifyProvider = async () => {
    setNotifyingProvider(true);
    try {
      await apiClient.post(`/api/service-referrals/${referralId}/notify-provider`);
      await loadMatches();
      onMatchingTriggered?.();
      setSuccessMessage('Provider notification sent successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to notify provider:', err);
      setError('Failed to notify provider');
    } finally {
      setNotifyingProvider(false);
    }
  };

  // Search Provider Directory using the full API
  const handleDirectorySearch = async (location: string, radiusMiles: number, filters: SearchFilters) => {
    setIsSearchingDirectory(true);
    setError(null);
    try {
      const response = await providerDirectoryAPI.search(location, radiusMiles, filters);

      // Filter out providers already in matches
      const existingIds = new Set(matches.map(m => String(m.provider_id)));
      const filteredProviders = response.providers.filter(p => !existingIds.has(p.id));

      setDirectoryProviders(filteredProviders);
    } catch (err) {
      console.error('Failed to search providers:', err);
      setError('Failed to search Provider Directory');
      setDirectoryProviders([]);
    } finally {
      setIsSearchingDirectory(false);
    }
  };

  // Handle provider click in directory
  const handleDirectoryProviderClick = (provider: Provider) => {
    setSelectedDirectoryProvider(provider);
  };

  // Add provider from directory (full Provider type)
  const handleAddProviderFromDirectory = async (provider: Provider) => {
    setAddingProvider(provider.id);
    setError(null);
    try {
      const address = [provider.street_address, provider.city, provider.state, provider.zip_code]
        .filter(Boolean)
        .join(', ');

      await apiClient.post(`/api/service-referrals/${referralId}/matches/add-provider`, {
        provider_id: provider.id,
        provider_name: provider.organization_name,
        provider_type: 'provider_directory',
        provider_phone: provider.primary_phone,
        provider_email: provider.primary_email,
        provider_address: address,
        distance_miles: provider.distance_miles,
        match_score: 80.0, // Default score for manually added
      });

      await loadMatches();
      setShowProviderDirectory(false);
      setDirectoryProviders([]);
      setSelectedDirectoryProvider(null);
      setSuccessMessage(`Added provider: ${provider.organization_name}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to add provider:', err);
      setError(err.response?.data?.detail || 'Failed to add provider');
    } finally {
      setAddingProvider(null);
    }
  };

  // State for tracked communications
  const [sendingCommunication, setSendingCommunication] = useState<{matchId: number, channel: string} | null>(null);

  // Send tracked communication (email, phone, fax) - PHI shielded until accepted
  const handleSendTrackedCommunication = async (
    match: ProviderMatch,
    channel: 'email' | 'phone' | 'fax' | 'voice_ai',
    contactValue?: string
  ) => {
    setSendingCommunication({ matchId: match.id, channel });
    setError(null);

    try {
      const response = await apiClient.post(
        `/api/service-referrals/${referralId}/matches/${match.id}/communicate`,
        {
          channel,
          contact_value: contactValue || (
            channel === 'email' ? match.provider_email :
            channel === 'fax' ? match.provider_fax :
            match.provider_phone
          ),
          // PHI is shielded - only include non-PHI info until provider accepts
          include_phi: match.phi_released || match.response_status === 'accepted',
          message_type: match.notified_at ? 'follow_up' : 'initial_outreach',
        }
      );

      if (response.data.success) {
        setSuccessMessage(
          `${channel === 'voice_ai' ? 'Voice AI call' : channel.charAt(0).toUpperCase() + channel.slice(1)} ` +
          `sent to ${match.provider_name}. Communication logged.`
        );
        await loadMatches();
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error(`Failed to send ${channel}:`, err);
      setError(err.response?.data?.detail || `Failed to send ${channel}`);
    } finally {
      setSendingCommunication(null);
    }
  };

  // Initiate Voice AI call to provider
  const handleInitiateVoiceCall = async (match: ProviderMatch) => {
    if (!match.provider_phone) {
      setError('Provider phone number is required for voice calls');
      return;
    }

    await handleSendTrackedCommunication(match, 'voice_ai', match.provider_phone);
  };

  // Remove provider from matches
  const handleRemoveProvider = async (matchId: number, providerName: string) => {
    if (!confirm(`Remove ${providerName} from matches?`)) return;

    setRemovingProvider(matchId);
    setError(null);
    try {
      await apiClient.delete(`/api/service-referrals/${referralId}/matches/${matchId}`);
      await loadMatches();
      setSuccessMessage(`Removed provider: ${providerName}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to remove provider:', err);
      setError(err.response?.data?.detail || 'Failed to remove provider');
    } finally {
      setRemovingProvider(null);
    }
  };

  // Get score color
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-blue-600 bg-blue-50';
    if (score >= 40) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  };

  // Get response status badge
  const getResponseStatusBadge = (status?: string) => {
    switch (status) {
      case 'accepted':
        return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-200">Accepted</Badge>;
      case 'declined':
        return <Badge className="bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200">Declined</Badge>;
      case 'negotiated':
        return <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-200">Negotiating</Badge>;
      case 'pending':
        return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200">Pending Response</Badge>;
      default:
        return null;
    }
  };

  // Render provider card
  const renderProviderCard = (match: ProviderMatch) => {
    const isExpanded = expandedProvider === match.id;
    const isSelected = match.is_selected;

    return (
      <Card
        key={match.id}
        className={`
          transition-all cursor-pointer
          ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50/30' : 'hover:shadow-md'}
        `}
        onClick={() => setExpandedProvider(isExpanded ? null : match.id)}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            {/* Provider Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-gray-500">#{match.match_rank}</span>
                <h4 className="font-medium truncate">{match.provider_name}</h4>
                {isSelected && (
                  <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200 shrink-0">Selected</Badge>
                )}
                {getResponseStatusBadge(match.response_status)}
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <Badge variant="outline" className="shrink-0">
                  {match.provider_type === 'cbo' ? 'CBO' : 'Provider Directory'}
                </Badge>
                {match.distance_miles && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {match.distance_miles.toFixed(1)} mi
                  </span>
                )}
              </div>
            </div>

            {/* Score */}
            <div className="flex flex-col items-end gap-2">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(match.match_score)}`}>
                {Math.round(match.match_score)}%
              </div>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </div>
          </div>

          {/* Expanded Details */}
          {isExpanded && (
            <div className="mt-4 pt-4 border-t space-y-3" onClick={(e) => e.stopPropagation()}>
              {/* Score Breakdown */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                {match.distance_score !== undefined && (
                  <div>
                    <span className="text-gray-500">Distance Score</span>
                    <p className="font-medium">{Math.round(match.distance_score)}%</p>
                  </div>
                )}
                {match.service_score !== undefined && (
                  <div>
                    <span className="text-gray-500">Service Score</span>
                    <p className="font-medium">{Math.round(match.service_score)}%</p>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Overall Match</span>
                  <p className="font-medium">{Math.round(match.match_score)}%</p>
                </div>
              </div>

              {/* Contact Info */}
              {(match.provider_phone || match.provider_email || match.provider_address) && (
                <div className="space-y-1 text-sm">
                  {match.provider_phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="h-3 w-3" />
                      {match.provider_phone}
                    </div>
                  )}
                  {match.provider_email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="h-3 w-3" />
                      {match.provider_email}
                    </div>
                  )}
                  {match.provider_address && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="h-3 w-3" />
                      {match.provider_address}
                    </div>
                  )}
                </div>
              )}

              {/* Response Timeline */}
              {match.notified_at && (
                <div className="text-sm text-gray-600">
                  <Clock className="h-3 w-3 inline mr-1" />
                  Notified: {fmt.dateTime(match.notified_at)}
                  {match.response_at && (
                    <span className="ml-2">
                      | Responded: {fmt.dateTime(match.response_at)}
                    </span>
                  )}
                </div>
              )}

              {/* Actions */}
              {!readOnly && (
                <div className="flex items-center gap-2 flex-wrap">
                  {!isSelected && !match.response_status && (
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectProvider(match.id);
                      }}
                      disabled={selectingProvider === match.id}
                    >
                      {selectingProvider === match.id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      Select Provider
                    </Button>
                  )}

                  {/* Communication dropdown - tracked outreach with PHI shielding */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" className="gap-1.5">
                        <MessageSquare className="h-4 w-4" />
                        <span>Contact</span>
                        {match.communication_count != null && match.communication_count > 0 && (
                          <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 rounded-full">
                            {match.communication_count}
                          </span>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72">
                      {/* PHI Status Banner */}
                      <div className={`px-3 py-2 text-xs flex items-center gap-2 ${
                        match.phi_released || match.response_status === 'accepted'
                          ? 'bg-green-50 text-green-700 border-b border-green-100'
                          : 'bg-amber-50 text-amber-700 border-b border-amber-100'
                      }`}>
                        {match.phi_released || match.response_status === 'accepted' ? (
                          <>
                            <ShieldCheck className="h-3.5 w-3.5" />
                            <span>PHI Released - Full patient info included</span>
                          </>
                        ) : (
                          <>
                            <ShieldAlert className="h-3.5 w-3.5" />
                            <span>PHI Shielded - Limited info until acceptance</span>
                          </>
                        )}
                      </div>

                      {/* Email - Opens Dialog */}
                      {match.provider_email && (
                        <DropdownMenuItem
                          onClick={() => openCommunicationDialog(match, 'email')}
                        >
                          <Mail className="h-4 w-4 mr-2 text-blue-600" />
                          <div className="flex flex-col">
                            <span>Compose Email</span>
                            <span className="text-xs text-gray-500">{match.provider_email}</span>
                          </div>
                        </DropdownMenuItem>
                      )}

                      {/* SMS - Opens Dialog */}
                      {match.provider_phone && (
                        <>
                          <DropdownMenuItem
                            onClick={() => openCommunicationDialog(match, 'sms')}
                          >
                            <MessageSquare className="h-4 w-4 mr-2 text-green-600" />
                            <div className="flex flex-col">
                              <span>Send SMS</span>
                              <span className="text-xs text-gray-500">{match.provider_phone}</span>
                            </div>
                          </DropdownMenuItem>

                          {/* Log Phone Call - Opens Dialog */}
                          <DropdownMenuItem
                            onClick={() => openCommunicationDialog(match, 'phone')}
                          >
                            <Phone className="h-4 w-4 mr-2 text-orange-600" />
                            <div className="flex flex-col">
                              <span>Log Phone Call</span>
                              <span className="text-xs text-gray-500">{match.provider_phone}</span>
                            </div>
                          </DropdownMenuItem>

                          {/* Voice AI Call - Opens Dialog */}
                          <DropdownMenuItem
                            onClick={() => openCommunicationDialog(match, 'voice_ai')}
                          >
                            <PhoneCall className="h-4 w-4 mr-2 text-purple-600" />
                            <div className="flex flex-col">
                              <span>Voice AI Call</span>
                              <span className="text-xs text-gray-500">AI-powered outreach call</span>
                            </div>
                          </DropdownMenuItem>
                        </>
                      )}

                      {/* Fax - Direct send (no compose needed) */}
                      {match.provider_fax && (
                        <DropdownMenuItem
                          onClick={() => handleSendTrackedCommunication(match, 'fax')}
                          disabled={sendingCommunication?.matchId === match.id && sendingCommunication?.channel === 'fax'}
                        >
                          {sendingCommunication?.matchId === match.id && sendingCommunication?.channel === 'fax' ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Printer className="h-4 w-4 mr-2 text-orange-600" />
                          )}
                          <div className="flex flex-col">
                            <span>Send Secure Fax</span>
                            <span className="text-xs text-gray-500">{match.provider_fax}</span>
                          </div>
                        </DropdownMenuItem>
                      )}

                      {/* No contact info */}
                      {!match.provider_email && !match.provider_phone && !match.provider_fax && (
                        <DropdownMenuItem disabled className="text-gray-500">
                          <AlertCircle className="h-4 w-4 mr-2" />
                          No contact information available
                        </DropdownMenuItem>
                      )}

                      {/* Communication History Link */}
                      {match.communication_count && match.communication_count > 0 && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => openCommunicationDialog(match, 'email')}
                            className="text-gray-600"
                          >
                            <History className="h-4 w-4 mr-2" />
                            <div className="flex flex-col">
                              <span>View Communication History</span>
                              <span className="text-xs text-gray-500">
                                {match.communication_count} attempt{match.communication_count > 1 ? 's' : ''}
                                {match.last_communication_at && ` · Last: ${fmt.short(match.last_communication_at)}`}
                              </span>
                            </div>
                          </DropdownMenuItem>
                        </>
                      )}

                      <DropdownMenuSeparator />
                      <div className="px-3 py-2 text-xs text-gray-500">
                        All communications are logged and tracked for compliance
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Remove button - only for pending providers that aren't selected */}
                  {!isSelected && (!match.response_status || match.response_status === 'pending') && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveProvider(match.id, match.provider_name);
                      }}
                      disabled={removingProvider === match.id}
                    >
                      {removingProvider === match.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Loading matches...</span>
        </CardContent>
      </Card>
    );
  }

  const selectedMatch = matches.find(m => m.is_selected);
  const canNotifyProvider = selectedMatch && !selectedMatch.notified_at && referral.status === 'matched';

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Provider Matching
            </CardTitle>
            <CardDescription>
              {matches.length > 0
                ? `${matches.length} providers matched based on location, services, and performance`
                : 'Run matching to find suitable providers'}
            </CardDescription>
          </div>
          {!readOnly && (
            <div className="flex items-center gap-2">
              {/* Add Provider from Directory - Opens Full Experience */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowProviderDirectory(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Provider
              </Button>

              {/* Auto-Match Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleTriggerMatching}
                disabled={matchingInProgress}
              >
                {matchingInProgress ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {matches.length > 0 ? 'Re-Match' : 'Find Providers'}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!readOnly && <WrenApprovalBanner referralId={referralId} onChanged={loadMatches} />}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {successMessage && (
          <Alert className="bg-green-50 border-green-200">
            <Check className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
          </Alert>
        )}

        {/* Selected Provider Status */}
        {selectedMatch && (
          <Alert className="bg-blue-50 border-blue-200">
            <Check className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>{selectedMatch.provider_name}</strong> is selected.
              {selectedMatch.response_status === 'pending' && ' Awaiting provider response.'}
              {selectedMatch.response_status === 'accepted' && ' Provider has accepted the referral.'}
              {selectedMatch.response_status === 'declined' && ' Provider declined. Select another provider.'}
              {!selectedMatch.notified_at && canNotifyProvider && (
                <Button
                  size="sm"
                  className="ml-4"
                  onClick={handleNotifyProvider}
                  disabled={notifyingProvider}
                >
                  {notifyingProvider ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Notify Provider
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Match List */}
        {matches.length > 0 ? (
          <div className="space-y-3">
            {matches.map(renderProviderCard)}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No providers matched yet</p>
            <p className="text-sm mt-1">
              Click &quot;Find Providers&quot; to run auto-matching based on location and service needs
            </p>
          </div>
        )}
      </CardContent>

      {/* Matching Info Footer */}
      {matches.length > 0 && (
        <CardFooter className="text-xs text-gray-500 border-t pt-4">
          <Shield className="h-3 w-3 mr-1" />
          Matching uses geographic proximity, service alignment, and provider performance metrics
        </CardFooter>
      )}

      {/* Full Provider Directory Modal */}
      {showProviderDirectory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-50 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Add Provider from Directory</h2>
                <p className="text-sm text-gray-600">
                  Search for providers by location, services, or organization type
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowProviderDirectory(false);
                  setDirectoryProviders([]);
                  setSelectedDirectoryProvider(null);
                }}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Search Panel */}
                <div className="lg:col-span-4">
                  <ProviderSearch onSearch={handleDirectorySearch} isSearching={isSearchingDirectory} />

                  {/* Results count */}
                  {directoryProviders.length > 0 && (
                    <Card className="mt-4">
                      <CardContent className="p-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Providers found:</span>
                          <span className="font-medium">{directoryProviders.length}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Click on a provider to view details and add to referral
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Results Panel */}
                <div className="lg:col-span-8">
                  {directoryProviders.length > 0 ? (
                    <ProviderResults
                      providers={directoryProviders}
                      isLoading={isSearchingDirectory}
                      onProviderClick={handleDirectoryProviderClick}
                      onGetDirections={(p) => {
                        const dest = `${p.street_address}, ${p.city}, ${p.state} ${p.zip_code}`;
                        window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`, '_blank');
                      }}
                    />
                  ) : (
                    <div className="bg-white rounded-lg shadow-md p-12 text-center">
                      <Building2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Search Provider Directory
                      </h3>
                      <p className="text-gray-600 max-w-md mx-auto">
                        Enter a location (ZIP code, city, or address) to find providers.
                        Use advanced filters to narrow by organization type, services, insurance, and more.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-white border-t border-gray-200 p-4 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowProviderDirectory(false);
                  setDirectoryProviders([]);
                  setSelectedDirectoryProvider(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Provider Detail Modal */}
      {selectedDirectoryProvider && (
        <ProviderDetail
          provider={selectedDirectoryProvider}
          onClose={() => setSelectedDirectoryProvider(null)}
          onGetDirections={(p) => {
            const dest = `${p.street_address}, ${p.city}, ${p.state} ${p.zip_code}`;
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`, '_blank');
          }}
          onSelectProvider={handleAddProviderFromDirectory}
          showSelectButton={true}
        />
      )}

      {/* Provider Communication Dialog */}
      {selectedMatchForCommunication && (
        <ProviderCommunicationDialog
          isOpen={communicationDialogOpen}
          onClose={() => {
            setCommunicationDialogOpen(false);
            setSelectedMatchForCommunication(null);
          }}
          referralId={referralId}
          match={selectedMatchForCommunication}
          onCommunicationSent={handleCommunicationSent}
          initialChannel={initialCommunicationChannel}
        />
      )}
    </Card>
  );
}

export default ProviderMatchingPanel;
